import { Controller } from 'stimulus'
import Swiper, { Autoplay, Navigation } from 'swiper'

Swiper.use([Autoplay, Navigation])

export default class extends Controller {

  connect() {
    this.slides = this.element.querySelectorAll('.swiper-slide')

    if (this.slides.length > 1) {
      this.initSlider()
    }

    this.initPlayPauseButtons()
    this.updateSlideAccessibility()

    this.mediaQuery = window.matchMedia('(min-width: 768px)')
    this.handleBreakpointChange = () => this.setVideoSources()
    this.mediaQuery.addEventListener('change', this.handleBreakpointChange)
    this.setVideoSources()
  }

  initPlayPauseButtons() {
    this.element.querySelectorAll('.js-heroCarouselPlayPause').forEach(button => {
      const video = button.closest('.swiper-slide')?.querySelector('video')
      if (!video) return

      this.syncPlayPauseButtonState(button, video)
      video.addEventListener('play', () => this.syncPlayPauseButtonState(button, video))
      video.addEventListener('pause', () => this.syncPlayPauseButtonState(button, video))
      button.addEventListener('click', (e) => this.toggleVideo(e))
    })
  }

  toggleVideo(e) {
    const button = e.currentTarget
    const video = button.closest('.swiper-slide')?.querySelector('video')
    if (!video) return

    if (video.paused) {
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise.catch(() => this.syncPlayPauseButtonState(button, video))
      }
    } else {
      video.pause()
    }
  }

  syncPlayPauseButtonState(button, video) {
    const isPlaying = !video.paused
    button.dataset.playing = isPlaying ? 'true' : 'false'
    button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video')
  }

  initSlider() {
    this.swiper = new Swiper(this.element.querySelector('.js-heroCarouselSlider'), {
      slidesPerView: 1,
      loop: true,
      speed: 800,
      navigation: {
        nextEl: this.element.querySelector('.js-heroCarouselNext'),
        prevEl: this.element.querySelector('.js-heroCarouselPrev'),
      },
      on: {
        slideChangeTransitionEnd: () => {
          this.updateSlideAccessibility()
          this.manageVideoPlayback()
        },
      },
    })
  }

  setVideoSources() {
    const isDesktop = this.mediaQuery.matches
    const prefix = isDesktop ? 'desktop' : 'mobile'
    const fallback = isDesktop ? 'mobile' : 'desktop'

    this.element.querySelectorAll('video[data-desktop-mp4], video[data-mobile-mp4], video[data-desktop-webm], video[data-mobile-webm]').forEach(video => {
      const src = video.dataset[`${prefix}Webm`] || video.dataset[`${prefix}Mp4`]
        || video.dataset[`${fallback}Webm`] || video.dataset[`${fallback}Mp4`]
      const poster = video.dataset[`${prefix}Poster`] || video.dataset[`${fallback}Poster`]
      const shouldUpdateSrc = video.src !== src
      const shouldUpdatePoster = (video.getAttribute('poster') || '') !== (poster || '')

      if (!src) return
      if (!shouldUpdateSrc && !shouldUpdatePoster) return

      if (shouldUpdatePoster) {
        video.poster = poster || ''
      }
      if (shouldUpdateSrc) {
        video.src = src
        video.load()
      }

      if (this.isActiveSlideVideo(video)) {
        video.play().catch(() => {})
      }
    })
  }

  manageVideoPlayback() {
    this.element.querySelectorAll('.swiper-slide video').forEach(video => {
      if (this.isActiveSlideVideo(video)) {
        video.play().catch(() => {})
      } else {
        video.pause()
        video.currentTime = 0
      }
    })
  }

  isActiveSlideVideo(video) {
    const slide = video.closest('.swiper-slide')
    if (!slide) return false
    if (!this.swiper) return true
    return slide.classList.contains('swiper-slide-active')
  }

  updateSlideAccessibility() {
    const slides = this.element.querySelectorAll('.swiper-slide')
    if (slides.length === 1) {
      return
    }

    slides.forEach(slide => {
      const isActive = slide.classList.contains('swiper-slide-active')
      slide.setAttribute('aria-hidden', !isActive)

      slide.querySelectorAll('button, a, [tabindex]').forEach(el => {
        el.setAttribute('tabindex', isActive ? '0' : '-1')
      })
    })
  }

  disconnect() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleBreakpointChange)
    }
    if (this.swiper) {
      this.swiper.destroy()
    }
  }
}
